import type { TFunction } from 'i18next';
import { getApiErrorCode } from '@/api/errors';
import type { ApiError } from '@/lib/agent.types';

/**
 * Translate error message from API response
 *
 * Handles:
 * - i18n keys from backend (e.g., "errors.validation.required")
 * - Server errors (5xx) with generic user-friendly messages
 * - Direct error messages
 * - HTTP status code fallbacks
 *
 * @param error - API error object from axios interceptor
 * @param t - Translation function from i18next
 * @returns Translated error message
 *
 * @example
 * ```ts
 * const message = translateErrorMessage(error, t);
 * toast.error(message);
 * ```
 */
/**
 * Metadata values can be string arrays (every offending value of a bulk
 * import). i18next would render those via Array#toString ("a@x.io,b@y.io") —
 * join with ", " so lists read naturally in interpolated messages.
 */
export function toInterpolationValues(
  metadata: ApiError['metadata']
): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(metadata ?? {}).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(', ') : value
    ])
  );
}

/** The backend's error code translated with its metadata when the code has a text, else the fallback key's text. */
export function translateErrorCode(error: unknown, fallbackKey: string, t: TFunction): string {
  const code = getApiErrorCode(error);
  if (code) {
    const translated = t(code, toInterpolationValues((error as Partial<ApiError>).metadata));
    if (translated !== code) return translated;
  }
  return t(fallbackKey);
}

export function translateErrorMessage(error: ApiError, t: TFunction): string {
  // For server errors (5xx), use generic user-friendly messages
  // instead of exposing technical details to users
  if (error.statusCode >= 500 && error.statusCode < 600) {
    return getHttpStatusMessage(error.statusCode, t);
  }

  // Try to use the error message from the API
  if (error.message) {
    // Check if it's an i18n key (starts with "errors." or contains dots suggesting a key path)
    if (
      error.message.startsWith('errors.') ||
      (error.message.includes('.') && !error.message.includes(' '))
    ) {
      // Try to translate it with metadata interpolation
      const translated = t(error.message, toInterpolationValues(error.metadata));
      // If translation returned the key itself, the key doesn't exist
      if (translated !== error.message) {
        return translated;
      }
    }

    // Otherwise return the message as is (already in user-friendly format)
    return error.message;
  }

  // Fallback to HTTP status code message
  if (error.statusCode) {
    return getHttpStatusMessage(error.statusCode, t);
  }

  // Ultimate fallback
  return t('errors.unknown');
}

/**
 * Get user-friendly message for HTTP status code
 *
 * @param statusCode - HTTP status code
 * @param t - Translation function
 * @returns Translated status message
 *
 * @example
 * ```ts
 * const message = getHttpStatusMessage(404, t); // "Not found"
 * ```
 */
export function getHttpStatusMessage(statusCode: number, t: TFunction): string {
  // Check for specific status code translation
  const key = `errors.http.${statusCode}`;
  const translated = t(key);

  // If translation exists, use it
  if (translated !== key) {
    return translated;
  }

  // Fallback to range-based messages
  if (statusCode >= 500) {
    return t('errors.http.500', { defaultValue: 'Server error' });
  }

  if (statusCode >= 400) {
    return t('errors.http.400', { defaultValue: 'Bad request' });
  }

  return t('errors.unknown');
}

/**
 * Check if error contains field-level validation errors
 *
 * Works with any HTTP status code (400, 422, etc.) as long as
 * the error includes field-level validation data.
 *
 * @param error - API error object
 * @returns True if error contains validation field errors
 *
 * @example
 * ```ts
 * if (isValidationError(error)) {
 *   setFormValidationErrors(error.errors, setError, t);
 * }
 * ```
 */
export function isValidationError(error: ApiError): boolean {
  return !!error.errors && Object.keys(error.errors).length > 0;
}

/**
 * Check if error is a network error (no response from server)
 *
 * @param error - API error object
 * @returns True if network error
 *
 * @example
 * ```ts
 * if (isNetworkError(error)) {
 *   toast.error(t('errors.network'));
 * }
 * ```
 */
export function isNetworkError(error: ApiError): boolean {
  return error.statusCode === 0 || error.error === 'Network Error';
}

/**
 * Check if error is a server error (5xx)
 *
 * @param error - API error object
 * @returns True if server error
 */
export function isServerError(error: ApiError): boolean {
  return error.statusCode >= 500 && error.statusCode < 600;
}

/**
 * Check if error is a client error (4xx)
 *
 * @param error - API error object
 * @returns True if client error
 */
export function isClientError(error: ApiError): boolean {
  return error.statusCode >= 400 && error.statusCode < 500;
}
